import { IHeadersDataPagination } from "interfaces/pagination";
import { IColumnMeta } from "interfaces/table";
import { useEffect, useState, useCallback, useMemo } from "react";
import DialogModel from "shared/DialogModel";
import {
  dateCreatedAtBodyTemplate,
  dateUpdatedAtBodyTemplate,
  statusActiveBodyTemplate,
} from "utils/bodyTemlates";
import { Box } from "@chakra-ui/react";
import Card from "components/card/Card";
import Table from "shared/Table";
import PaginatorPages from "shared/Paginator";
import { environments } from "constants/environments";
import { useAuth } from "contexts/auth";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import AlertBox from "shared/AlertBox";
import TabBar from "shared/TabBar";
import { ICategory } from "interfaces/category";
import UpdateModule from "./components/UpdateModule";
import AddModule from "./components/AddModule";
import { FaEye } from "react-icons/fa";

const Modules = () => {
  const [data, setData] = useState<ICategory[]>([]);
  const [pagination, setPagination] = useState<IHeadersDataPagination>({
    paginationcurrent: 1,
    paginationlimit: 20,
    paginationtotal: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const { sharedData } = useAuth();

  const categoryAPI =
    environments.domain + sharedData?.apisList?.WebSite?.Category?.General;

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const response: IApiMetaData = await callApi(
        "GET",
        categoryAPI,
        {},
        pagination,
        { populatefields: "program", populateselected: "name" }
      );
      if (response.Status === 200) {
        setPagination((prevPagination) => ({
          ...prevPagination,
          paginationtotal: response.Pagination.total,
        }));
        setData(response.Data.Content);
        setIsError(false);
      } else {
        setIsError(true);
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setLoading(false);
    }
  }, [categoryAPI, pagination.paginationcurrent, pagination.paginationlimit]);

  useEffect(() => {
    getData();
  }, [pagination.paginationcurrent, pagination.paginationlimit]);

  // Memoize the columns to prevent re-creating them on every render
  const columns = useMemo(
    (): IColumnMeta<ICategory>[] => [
      {
        field: "name",
        header: "Name",
      },
      {
        field: "program",
        header: "Programs",
        body: (data: ICategory | any) => {
          return (
            <>
              {Array.isArray(data?.program) && data.program.length > 0 ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {`${data.program.length} Programs `}
                    <DialogModel
                      title="Programs"
                      customButton={
                        <FaEye
                          style={{
                            cursor: "pointer",
                            color: "#007bff",
                            fontSize: "1.2em",
                            display: "flex",
                          }}
                        />
                      }
                      width="40vw"
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {data.program?.map((program: any, index: number) => (
                          <span
                            key={index}
                            style={{
                              backgroundColor: "#f5f5f5",
                              padding: "5px 10px",
                              borderRadius: "5px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {program.name}
                          </span>
                        )) || "No Programs"}
                      </div>
                    </DialogModel>
                  </div>
                </>
              ) : (
                "No Programs"
              )}
            </>
          );
        },
      },
      {
        field: "isActive",
        header: "Status",
        body: statusActiveBodyTemplate,
      },
      {
        field: "createdAt",
        header: "CreatedAt",
        body: dateCreatedAtBodyTemplate,
      },
      {
        field: "updatedAt",
        header: "UpdatedAt",
        body: dateUpdatedAtBodyTemplate,
      },
      {
        field: "edit",
        header: "Edit",
        body: (rowData: ICategory) => (
          <DialogModel
            title="Update Module"
            icon="pi pi-cog"
            width="60vw"
            severity="secondary"
          >
            <UpdateModule rowData={rowData} onSave={getData} />
          </DialogModel>
        ),
      },
    ],
    [getData]
  );

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card className="mb-4">
        <TabBar>
          <DialogModel title="Add Module" label="Add Module" width="60vw">
            <AddModule onSave={getData} />
          </DialogModel>
        </TabBar>
      </Card>

      <Card>
        <div style={{ width: "100%", marginTop: "20px", marginBottom: "20px" }}>
          {!isError ? (
            <>
              <Box>
                <Table data={data} columns={columns} loading={loading} />
                <PaginatorPages
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </Box>
            </>
          ) : (
            <div className="grid justify-content-center align-content-center">
              <AlertBox
                status="error"
                description="Something went wrong. Please try again later."
                title="Error"
              />
            </div>
          )}
        </div>
      </Card>
    </Box>
  );
};

export default Modules;
